import { MessageCircle, Users, HeartHandshake } from "lucide-react";
import { useReference } from "@/lib/reference";

const PILLARS = [
  {
    key: "Communicate",
    icon: MessageCircle,
    color: "#5D3A1F",
    tagline: "How we say what we mean",
    description: "Professional communication, active listening, and the courage to navigate difficult conversations with grace.",
  },
  {
    key: "Collaborate",
    icon: Users,
    color: "#C28A2E",
    tagline: "How we work together",
    description: "Teamwork, accountability, mutual respect, and conflict resolution that strengthens rather than divides.",
  },
  {
    key: "Serve",
    icon: HeartHandshake,
    color: "#8B5A2B",
    tagline: "How we lift others",
    description: "Service excellence — internal and external — that transforms transactions into lasting relationships.",
  },
];

const CCSFrameworkPage = () => {
  const ref = useReference();
  return (
    <div className="space-y-8" data-testid="ccs-framework-page">
      <div className="ccs-card ccs-grain p-7 lg:p-10">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">The CCS Framework</div>
        <h1 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight mt-2 text-[#2A1810] leading-[1.05]">
          Communicate. Collaborate. <span className="text-[#C28A2E]">Serve.</span>
        </h1>
        <p className="text-[#6B5944] mt-4 max-w-2xl leading-relaxed">
          A practical methodology for cultivating workplace cultures of excellence. Every workshop, proposal,
          and engagement is organized around these three foundational pillars.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {PILLARS.map((p) => {
          const topics = ref.ccs_framework[p.key] || [];
          return (
            <div key={p.key} className="ccs-card overflow-hidden" data-testid={`ccs-pillar-${p.key.toLowerCase()}`}>
              <div className="p-6 lg:p-7" style={{ background: `${p.color}0a` }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: p.color }}>
                  <p.icon className="w-5 h-5 text-white" strokeWidth={1.7} />
                </div>
                <div className="font-display text-3xl font-semibold mt-5 text-[#2A1810]">{p.key}</div>
                <div className="text-sm text-[#6B5944] mt-1.5">{p.tagline}</div>
                <p className="text-sm text-[#6B5944] mt-3 leading-relaxed">{p.description}</p>
              </div>
              <div className="p-6 lg:p-7 border-t border-[#EBE3D2]">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold mb-3">Modules</div>
                <ul className="space-y-2">
                  {topics.map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-[#2A1810]">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: p.color }} />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      <div className="ccs-card p-7 lg:p-9">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Resource library</div>
        <h2 className="font-display text-2xl font-semibold tracking-tight mt-2">Coming soon</h2>
        <p className="text-[#6B5944] mt-2 max-w-2xl">
          Future-ready workspace for organizing your training materials, slide decks, handouts, and activity templates
          under each CCS pillar. Upload, tag, and quickly retrieve everything you need to deliver world-class workshops.
        </p>
      </div>
    </div>
  );
};

export default CCSFrameworkPage;
